import type { StatementSectionDto } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';

/**
 * المقارنة بفترة سابقة تُبنى فى الواجهة من طلبين للـ API نفسه — لا رقم جديد يُحسب
 * هنا سوى الفرق، فعمود المقارنة هو بالضبط ما كانت القائمة ستعرضه لو طُلبت لتلك الفترة.
 */

/**
 * يُرجع التاريخ نفسه قبل/بعد عدد من السنوات. 29 فبراير فى سنة غير كبيسة يصير 28،
 * حتى تبقى نهاية فبراير نهاية فبراير ولا تنزلق إلى مارس.
 */
export function shiftYears(date: string, years: number): string {
  const [y, m, d] = date.split('-');
  const year = Number(y) + years;
  const month = Number(m);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const day = Math.min(Number(d), lastDay);
  return (
    String(year).padStart(4, '0') +
    '-' +
    String(month).padStart(2, '0') +
    '-' +
    String(day).padStart(2, '0')
  );
}

export interface ComparedRow {
  code: string;
  nameAr: string;
  current: string;
  prior: string;
  change: string;
}

export interface ComparedSection {
  rows: ComparedRow[];
  total: string;
  priorTotal: string;
  change: string;
}

export function amountChange(current: string, prior: string): string {
  return Money.of(current).sub(prior).normalize().toString();
}

/**
 * يدمج قسمين بكود الحساب: حساب تحرك فى سنة دون الأخرى يظهر بصفر فى الأخرى،
 * فلا يختفى سطر من المقارنة. الترتيب بالكود — أكواد الدليل كلها بطول واحد.
 */
export function compareSections(
  current: StatementSectionDto,
  prior: StatementSectionDto,
): ComparedSection {
  const byCode = new Map<string, ComparedRow>();
  for (const row of current.rows) {
    byCode.set(row.code, {
      code: row.code,
      nameAr: row.nameAr,
      current: row.amount,
      prior: '0',
      change: '0',
    });
  }
  for (const row of prior.rows) {
    const existing = byCode.get(row.code);
    if (existing) existing.prior = row.amount;
    else
      byCode.set(row.code, {
        code: row.code,
        nameAr: row.nameAr,
        current: '0',
        prior: row.amount,
        change: '0',
      });
  }
  const rows = [...byCode.values()]
    .sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))
    .map((row) => ({ ...row, change: amountChange(row.current, row.prior) }));
  return {
    rows,
    total: current.total,
    priorTotal: prior.total,
    change: amountChange(current.total, prior.total),
  };
}

/** المقارنة: بلا، أو الفترة السابقة بنفس الطول، أو نفس الفترة من العام الماضى. */
export type Comparison = 'none' | 'previous' | 'last_year';

const DAY = 86_400_000;

function toTime(date: string): number {
  return Date.parse(date + 'T00:00:00Z');
}

function fromTime(time: number): string {
  return new Date(time).toISOString().slice(0, 10);
}

function lastDayOf(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function isMonthEnd(date: string): boolean {
  const [y, m, d] = date.split('-').map(Number);
  return d === lastDayOf(y!, m!);
}

/**
 * يُرجع التاريخ قبل/بعد عدد من الشهور. نهاية الشهر تبقى نهاية الشهر (31 مارس قبل شهر =
 * 28 أو 29 فبراير، و28 فبراير قبل شهر = 31 يناير).
 */
export function shiftMonths(date: string, months: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const index = y! * 12 + (m! - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const last = lastDayOf(year, month);
  const day = isMonthEnd(date) ? last : Math.min(d!, last);
  return (
    String(year).padStart(4, '0') +
    '-' +
    String(month).padStart(2, '0') +
    '-' +
    String(day).padStart(2, '0')
  );
}

/**
 * الفترة المقابلة لقائمة الدخل. «السابقة» تنتهى قبل البداية بيوم وبنفس الطول: بالشهور
 * حين تكون الفترة شهورًا كاملة (الربع الثانى يقابله الأول)، وإلا بالأيام.
 */
export function priorPeriod(
  from: string,
  to: string,
  comparison: Comparison,
): { from: string; to: string } | null {
  if (comparison === 'none') return null;
  if (comparison === 'last_year') return { from: shiftYears(from, -1), to: shiftYears(to, -1) };
  if (from.endsWith('-01') && isMonthEnd(to)) {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    const months = (ty! - fy!) * 12 + (tm! - fm!) + 1;
    return { from: shiftMonths(from, -months), to: fromTime(toTime(from) - DAY) };
  }
  const days = Math.round((toTime(to) - toTime(from)) / DAY) + 1;
  return {
    from: fromTime(toTime(from) - days * DAY),
    to: fromTime(toTime(from) - DAY),
  };
}

/** تاريخ المقارنة للمركز المالى: قبل شهر (نهاية الشهر السابق لنهاية شهر)، أو قبل سنة. */
export function priorDate(asOf: string, comparison: Comparison): string | null {
  if (comparison === 'none') return null;
  return comparison === 'last_year' ? shiftYears(asOf, -1) : shiftMonths(asOf, -1);
}
